import type { ReactNode } from 'react';
import type { EventDetailDto } from '@/lib/models';
import { formatVolume } from '@/lib/binary-market';
import graphIcon from '@/assets/graph-icon.svg';
import peoplesIcon from '@/assets/peoples-icon.svg';
import calendarIcon from '@/assets/calendar-icon.svg';

export interface MarketPageHeaderProps {
  /** The event whose header values to render (event-level / aggregated). */
  event: EventDetailDto;
  /** Overrides the accent category label (e.g. "Sports – NBA"); defaults to the event's category code. */
  categoryLabel?: string;
  /** Optional line under the title (e.g. a sports event's start time). */
  subtitle?: ReactNode;
  /** Optional block between the title and the stat strip (e.g. the sports team legend). */
  beforeStats?: ReactNode;
  /** Closed-market header (PRD §7): a "Resolved" chip and "Closed" replacing "Expires". */
  resolved?: boolean;
}

/** Format an ISO timestamp as `MMM D, YY`, or `—`. */
function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}

/** Compact participant count, e.g. `12.4K`. */
function formatParticipants(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

/**
 * Market-page header (ARC-82 §5.2).
 *
 * Question + category, then a stat strip of total volume, participant count,
 * and resolution time. All values are event-level (aggregated across markets
 * by the API for multi-market events).
 */
export function MarketPageHeader({ event, categoryLabel, subtitle, beforeStats, resolved }: MarketPageHeaderProps) {
  // A single BINARY market shows the headline Yes chance under the question
  // (Figma 1697-76900) — resolved server-side. Multi-market / MULTI → none.
  // A resolved page drops it (the Result section owns the final probability).
  const market = event.markets.length === 1 ? event.markets[0] : undefined;
  const chance = !resolved && market?.renderKind === 'BINARY' ? market.chancePercent : null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        {/* Category eyebrow: placeholder avatar + label in New Purple (Figma 6094/5138). */}
        <span className="flex items-center gap-2 text-[16px] text-purple">
          <span aria-hidden className="size-8 shrink-0 rounded-full bg-border" />
          <span className="min-w-0 flex-1 truncate">{categoryLabel ?? event.categoryCode}</span>
          {resolved && (
            // Figma 6692:172810 — 87×25 pill, 16px semibold new-purple on #f1e8f8.
            <span className="flex h-[25px] shrink-0 items-center rounded-pill bg-chip-purple px-3 text-[16px] font-semibold capitalize leading-none text-purple tracking-[-0.32px]">
              Resolved
            </span>
          )}
        </span>
        <h1 className="m-0 text-[20px] font-medium leading-snug text-ink">{event.name}</h1>
        {chance != null && <span className="text-sm font-semibold text-info">{chance}% Chance</span>}
        {subtitle}
      </div>

      {beforeStats}

      <div className="flex h-[50px] items-center rounded-[69px] bg-surface-soft px-3">
        <Stat icon={graphIcon} label="Volume" value={`$${formatVolume(event.volume)}`} />
        <span aria-hidden className="h-[34px] w-px shrink-0 bg-stroke-soft" />
        <Stat icon={peoplesIcon} label="Participants" value={formatParticipants(event.participantCount)} />
        <span aria-hidden className="h-[34px] w-px shrink-0 bg-stroke-soft" />
        <Stat icon={calendarIcon} label={resolved ? 'Closed' : 'Expires'} value={formatDate(event.resolutionTime)} />
      </div>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-1">
      <img src={icon} alt="" aria-hidden className="size-[22px] shrink-0" />
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-[14px] leading-tight text-ink">{label}</span>
        <span className="truncate text-[18px] font-medium leading-tight text-ink">{value}</span>
      </span>
    </div>
  );
}
